import { ClientSelectOption } from '@/components/select/SelectClient'
import {
  CLIENT_PARTY_TYPE,
  DATE_FORMATS,
  FCA_SERVICE_TYPE_NAMES,
  PANEL_STATUS,
  REFERRAL_TYPE_OPTION,
  SERVICE_AREA_ID,
} from '@/lib/constants'
import {
  convertDate,
  isValidDateFormat,
  normalizeDateOfBirth,
} from '@/lib/helpers/dateHelpers'
import referralFlowService from '@/lib/services/referralFlowService'
import { ClientDetailsFormValues } from '@/pages/referClient/steps/1_ClientDetails/types'
import {
  DISABLED_REASON,
  DisabledReason,
} from '@/pages/referClient/steps/2_ReferralDetails/components/SelectServiceFirm/constants'
import { REFERRAL_METHOD, ReferralMethod } from '@/redux/slices/webReferral'
import { ReferralTypeOption } from '@/types/misc'
import {
  CreateClientForReferralRequest,
  UpdateClientForReferralRequest,
} from '@/types/requests/clients'
import { CompanyTypeItem } from '@/types/responses/common-data'
import {
  ReferralFirmCollectionItem,
  ReferralFirmService,
} from '@/types/responses/companies'
import { ExistingAdviserReferralMetadataItem } from '@/types/responses/referral-metadata'

class WebReferralService {
  isBookCallForClientReferralType(
    referralTypeOption?: ReferralTypeOption,
  ): boolean {
    return (
      !!referralTypeOption &&
      referralTypeOption === REFERRAL_TYPE_OPTION.bookCallForClient
    )
  }

  isValidReferralTarget(value?: unknown): value is ReferralMethod {
    return (
      typeof value === 'string' && Object.keys(REFERRAL_METHOD).includes(value)
    )
  }

  getMostRestrictedServiceType(
    selectedServices: ReferralFirmCollectionItem['services'],
  ): string {
    if (selectedServices.length === 0) {
      return FCA_SERVICE_TYPE_NAMES.no
    }

    const servicesOrderedByRestrictionLevel = selectedServices.sort(
      (serviceA, serviceB) => {
        const serviceARestrictionLevel =
          serviceA.serviceType?.restrictionLevel || 0
        const serviceBRestrictionLevel =
          serviceB.serviceType?.restrictionLevel || 0

        return serviceBRestrictionLevel - serviceARestrictionLevel
      },
    )

    const mostRestrictedService = servicesOrderedByRestrictionLevel[0]

    return mostRestrictedService?.serviceType?.name || FCA_SERVICE_TYPE_NAMES.no
  }

  getFirmDisabledReason(options: {
    selectedFirm: ReferralFirmCollectionItem
    companyTypes: CompanyTypeItem[]
    selectedServiceAreaIds: number[]
  }): DisabledReason | undefined {
    const { selectedFirm, companyTypes, selectedServiceAreaIds } = options

    const selectedServices = selectedFirm.services.filter((service) =>
      selectedServiceAreaIds.includes(service.serviceArea.id),
    )

    const firmServiceAreaIds = selectedFirm.services.map(
      (service) => service.serviceArea.id,
    )

    const firmHasAllSelectedServices = selectedServiceAreaIds.every(
      (selectedServiceAreaId) => {
        return firmServiceAreaIds.includes(selectedServiceAreaId)
      },
    )

    const mostRestrictedServiceType =
      webReferralService.getMostRestrictedServiceType(selectedServices)

    const isApproved =
      selectedFirm.panelWithCurrentCompany.status === PANEL_STATUS.approved

    const companyType = companyTypes.find(
      (companyType) => companyType['@id'] === selectedFirm.companyType['@id'],
    )

    const isFcaFirm = companyType?.fca

    if (!firmHasAllSelectedServices && !isApproved) {
      return DISABLED_REASON.missingServiceAndUnapproved
    }

    if (!firmHasAllSelectedServices) {
      return DISABLED_REASON.missingService
    }

    if (!isApproved) {
      return DISABLED_REASON.unapproved
    }

    if (this.hasSelectedNonStandaloneServiceByItself(selectedServices)) {
      return DISABLED_REASON.nonStandaloneServicesHaveFinancialPlanSelected
    }

    if (isFcaFirm && mostRestrictedServiceType === FCA_SERVICE_TYPE_NAMES.no) {
      return DISABLED_REASON.fcaFirmRestrictedService
    }

    return
  }

  hasSelectedNonStandaloneServiceByItself(
    selectedServices: ReferralFirmService[],
  ) {
    const hasSelectedNonStandaloneService = selectedServices.some(
      (service) => !service.isStandaloneService,
    )

    const hasSelectedCreationOfAFinancialPlan = selectedServices.some(
      (service) =>
        service.serviceArea.id === SERVICE_AREA_ID.creationOfAFinancialPlan,
    )

    return (
      hasSelectedNonStandaloneService && !hasSelectedCreationOfAFinancialPlan
    )
  }

  buildCreateClientRequest(
    input: ClientDetailsFormValues,
  ): CreateClientForReferralRequest {
    const dateOfBirth = input.dateOfBirth
      ? normalizeDateOfBirth(input.dateOfBirth)
      : undefined

    const request: CreateClientForReferralRequest = {
      partyName: input.partyName,
      partyType: input.partyType,
      firstName: input.firstName || '',
      lastName: input.lastName || '',
      email: input.email,
      dateOfBirth,
      telephone: input.telephone || '',
      clientId: input.clientId || '',
      addressLine1: input.addressLine1 || '',
      city: input.city || '',
    }

    if (input.postcode) {
      request.postcode = input.postcode
    }

    return request
  }

  getPatchedFields(
    client: ClientSelectOption | null,
    formValues?: ClientDetailsFormValues,
  ): Array<keyof UpdateClientForReferralRequest> {
    const fields: Array<keyof UpdateClientForReferralRequest> = []

    // If we don't have a client, then no fields have been patched
    if (!client || !formValues) {
      return fields
    }

    if (!client.telephone && !!formValues.telephone) {
      fields.push('telephone')
    }

    if (!client.dateOfBirth && !!formValues.dateOfBirth) {
      fields.push('dateOfBirth')
    }

    if (!client.clientId && !!formValues.clientId) {
      fields.push('clientId')
    }

    if (!client.addressLine1 && !!formValues.addressLine1) {
      fields.push('addressLine1')
    }

    if (!client.city && !!formValues.city) {
      fields.push('city')
    }

    if (!client.postcode && !!formValues.postcode) {
      fields.push('postcode')
    }

    return fields
  }

  buildInitialClientDetailsValues(
    previousInput?: ClientDetailsFormValues,
  ): ClientDetailsFormValues {
    return {
      partyName: previousInput?.partyName || '',
      partyType: previousInput?.partyType || CLIENT_PARTY_TYPE.PERSON,
      firstName: previousInput?.firstName || '',
      lastName: previousInput?.lastName || '',
      email: previousInput?.email || '',
      telephone: previousInput?.telephone || '',
      dateOfBirth: webReferralService.getFormattedDateOfBirth(
        previousInput?.dateOfBirth,
      ),
      clientId: previousInput?.clientId || '',
      addressLine1: previousInput?.addressLine1 || '',
      city: previousInput?.city || '',
      postcode: previousInput?.postcode || '',
      circumstances: previousInput?.circumstances || '',
    }
  }

  // Get a date of birth in the DD/MM/YYYY format
  getFormattedDateOfBirth(dateOfBirth?: string | null): string | undefined {
    // Extract just the date part from the datetime string
    const date = (dateOfBirth || '').split('T')[0] || ''

    // No date, so return undefined
    if (!date) {
      return
    }

    // Date of birth format already valid, so return it
    if (isValidDateFormat(date, DATE_FORMATS.DAY_MONTH_YEAR)) {
      return date
    }

    // Date of birth format is YYYY-MM-DD, so convert it to DD/MM/YYYY
    if (isValidDateFormat(date, DATE_FORMATS.YEAR_MONTH_DAY)) {
      return convertDate(
        date,
        DATE_FORMATS.YEAR_MONTH_DAY,
        DATE_FORMATS.DAY_MONTH_YEAR,
      )
    }

    return undefined
  }

  getAdditionalReferralDetailsValidationSchema(options: {
    existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  }) {
    const { existingAdviserReferralMetadata } = options

    return {
      ...referralFlowService.getCommissionQuestionsValidationSchema({
        existingAdviserReferralMetadata,
      }),
      ...referralFlowService.getReferralJustificationValidationSchema({
        existingAdviserReferralMetadata,
      }),
    }
  }
}

const webReferralService = new WebReferralService()

export default webReferralService
